import React, { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  EmptyState,
  Icon,
  Illustration,
  Link,
  Pagination,
  RowsSkeletonLoader,
  toast,
  useSidePanel,
} from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useDeletePaymentLimitMutation } from 'hooks/mutations/useDeletePaymentLimitMutation'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import {
  LimitLevel,
  PaymentLimitAction,
  PaymentLimitSheetType,
  type Limit,
  type PaymentLimitActionType,
} from 'types/paymentLimits'
import { PaymentLimitCard } from './components/PaymentLimitCard'
import { PaymentLimitSheet } from './components/PaymentLimitSheet'
import { PaymentLimitSidePanel } from './components/PaymentLimitSidePanel'
import { groupLimitsByMethodAndCurrency } from './utils'

export const PaymentLimits = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { currentAccount } = useAccountsContext()
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useSearchablePagination(MIN_PAGINATION_SIZE)

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false)
  const [selectedLimit, setSelectedLimit] = useState<Limit | null>(null)

  const { mutate: deleteLimit, isPending: isDeleting } = useDeletePaymentLimitMutation()

  const {
    data: limitsData,
    isFetching,
    isError,
  } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    pageNumber: page,
    pageSize: rowsPerPage,
    level: LimitLevel.ALL_CUSTOMERS,
  })

  const limits = limitsData?.content ?? []
  const totalElements = limitsData?.totalElements ?? 0

  const groupedLimits = useMemo(() => groupLimitsByMethodAndCurrency(limits), [limits])

  const hasLimits = groupedLimits?.length > 0
  const showEmptyState = !isFetching && !isError && groupedLimits?.length === 0

  const {
    isSidePanelOpen,
    sidePanelData: selectedSidePanelLimit,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(limits)

  const handleCloseEditSheet = useCallback(() => {
    setIsEditSheetOpen(false)
    setSelectedLimit(null)
  }, [])

  const handleAction = useCallback(
    (action: PaymentLimitActionType, limit: Limit) => {
      switch (action) {
        case PaymentLimitAction.VIEW: {
          const index = limits?.findIndex(item => item?.id === limit?.id)
          if (index < 0) return
          updateSidePanel(index)
          setIsSidePanelOpen(true)
          break
        }
        case PaymentLimitAction.EDIT:
          setSelectedLimit(limit)
          setIsEditSheetOpen(true)
          break
        case PaymentLimitAction.DELETE:
          deleteLimit(limit.id, {
            onSuccess: () => {
              toast({ header: t('paymentLimits.limitDeletedSuccess'), variant: 'success' })
              queryClient.invalidateQueries({
                queryKey: [reactQueryName.PAYMENT_LIMITS],
              })
            },
            onError: () => {
              toast({ header: t('paymentLimits.limitDeleteError'), variant: 'error' })
            },
          })
          break
      }
    },
    [limits, setIsSidePanelOpen, updateSidePanel, deleteLimit, t, queryClient]
  )

  return (
    <>
      <PageHeader
        description={t('paymentLimits.pageDescription')}
        renderRightSection={
          <Button
            testId="add-limit-button"
            variant="secondary"
            iconElement={<Icon name="Plus" />}
            onClick={() => setIsAddSheetOpen(true)}
          >
            {t('paymentLimits.addLimit')}
          </Button>
        }
      />

      <Box paddingB={24}>
        <Callout>
          {t('paymentLimits.customerLimitsCallout')}{' '}
          <Link href="/customers" target="_self">
            {t('paymentLimits.customerLimitsCalloutLink')}
          </Link>
        </Callout>
      </Box>

      {isFetching && <RowsSkeletonLoader rows={3} />}

      {hasLimits && (
        <>
          <Box flex isFlexWrap gap={16}>
            {groupedLimits?.map(groupedLimit => (
              <Box
                key={`${groupedLimit.method}-${groupedLimit.currency}`}
                className="w-full md:w-[calc(50%-8px)]"
              >
                <PaymentLimitCard
                  groupedLimit={groupedLimit}
                  isDeleting={isDeleting}
                  onAction={handleAction}
                />
              </Box>
            ))}
          </Box>

          {totalElements > rowsPerPage && (
            <Box paddingT={24}>
              <Pagination
                testId="payment-limits-pagination"
                page={page}
                rowsPerPage={rowsPerPage}
                total={totalElements}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </Box>
          )}
        </>
      )}

      {showEmptyState && (
        <EmptyState
          testId="payment-limits-empty-state"
          LottiePlayer={<Illustration name="Welcome" />}
          title={t('paymentLimits.emptyStateTitle')}
          description={t('paymentLimits.emptyStateDescription')}
          PrimaryButton={
            <Button
              variant="secondary"
              iconElement={<Icon name="Plus" />}
              onClick={() => setIsAddSheetOpen(true)}
            >
              {t('paymentLimits.addLimit')}
            </Button>
          }
        />
      )}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        onClose={() => setIsAddSheetOpen(false)}
      />

      <PaymentLimitSheet
        isOpen={isEditSheetOpen}
        type={PaymentLimitSheetType.EDIT}
        limit={selectedLimit}
        onClose={handleCloseEditSheet}
      />

      <PaymentLimitSidePanel
        isOpen={isSidePanelOpen}
        limit={selectedSidePanelLimit ?? null}
        onClose={() => setIsSidePanelOpen(false)}
      />
    </>
  )
}
